import { format as ecFormat, graphic as ecGraphic } from "echarts";

const SERIES_HEIGHT_MIN_Y = 400;
const SERIES_HEIGHT_MAX_Y = 900;
const SERIES_STAGGER_MIN_Y = -400;
const SERIES_STAGGER_MAX_Y = 400;

const GRID_LAYOUT = {
    gridNames: ["height", "right_height", "stagger", "right_stagger", "acceleration", "right_acceleration"],
    topPercent: 5,
    heightPercent: 87.5,
    totalSpacingPercent: 7.5,
    left: 80,
    right: 40,
};

const EXCEEDANCE_CLASSES = ["height", "stagger", "acceleration"];

export const calculateGridHeight = (baseGridsOnly = false) => {
    const gridCount = GRID_LAYOUT.gridNames.length / (baseGridsOnly ? 1 : 2);
    return (GRID_LAYOUT.heightPercent - GRID_LAYOUT.totalSpacingPercent) / gridCount;
};

const thresholdMarkLines = {
    symbol: "none",
    lineStyle: {
        width: 1,
        type: [5, 10],
        opacity: 0.7,
    },
    label: {
        show: true,
        formatter: ({ data }) => data.name,
        position: "end",
    },
    data: [],
};

const generateGrids = () => {
    const { gridNames, topPercent, heightPercent, totalSpacingPercent, left, right } = GRID_LAYOUT;
    const spacingPercent = totalSpacingPercent / (gridNames.length - 1);
    const totalSpacing = heightPercent - totalSpacingPercent;
    const gridHeight = totalSpacing / gridNames.length;

    const grids = gridNames.map((grid, i) => {
        return {
            id: `${grid}_grid`,
            top: `${topPercent + i * (gridHeight + spacingPercent)}%`,
            left,
            right,
            height: `${gridHeight}%`,
        };
    });

    return [...grids, { id: "position_grid", top: "5%", left, right, bottom: "7.5%" }, { id: "overview_grid", top: "93.5%", left, right, height: "5.5%" }];
};

const renderExceedanceMarker = (params, api) => {
    const point = api.coord([api.value("peakTimestamp"), api.value("exceedance") + 32]);

    const startX = api.coord([api.value("startTimestamp"), 0])[0];
    const endX = api.coord([api.value("endTimestamp"), 0])[0];
    const yTop = params.coordSys.y;
    const yBottom = params.coordSys.y + params.coordSys.height;

    return {
        type: "group",
        children: [
            {
                type: "rect",
                shape: {
                    x: startX,
                    y: yTop,
                    width: endX - startX,
                    height: yBottom - yTop,
                },
                style: {
                    fill: "#eab308",
                    opacity: 0.25,
                },
            },
            {
                type: "line",
                shape: {
                    x1: startX,
                    y1: yTop,
                    x2: startX,
                    y2: yBottom,
                },
                style: { stroke: "#eab308", lineWidth: 1, lineDash: [4, 4], opacity: 0.5 },
            },
            {
                type: "line",
                shape: {
                    x1: endX,
                    y1: yTop,
                    x2: endX,
                    y2: yBottom,
                },
                style: { stroke: "#eab308", lineWidth: 1, lineDash: [4, 4], opacity: 0.5 },
            },
            {
                type: "text",
                style: {
                    text: "◆",
                    x: point[0],
                    y: params.coordSys.y,
                    textAlign: "center",
                    textVerticalAlign: "bottom",
                    fontSize: 16,
                    fill: "#eab308",
                },
            },
        ],
    };
};

export const option = {
    legend: {
        show: true,
        top: 10,
    },
    tooltip: {
        trigger: "axis",
        axisPointer: { type: "cross" },
    },
    axisPointer: {
        link: [
            {
                xAxisId: ["overview_x", "position_x"],
            },
            {
                xAxisId: ["height_x", "right_height_x"],
            },
            {
                xAxisId: ["stagger_x", "right_stagger_x"],
            },
            {
                xAxisId: ["acceleration_x", "right_acceleration_x"],
            },
        ],
    },
    grid: generateGrids(),
    xAxis: [
        {
            id: "height_x",
            gridId: "height_grid",
            type: "time",
            axisLine: { show: false },
            axisTick: { show: false },
            axisLabel: { show: false },
            splitLine: { show: false },
            axisPointer: {
                label: {
                    show: false,
                },
                lineStyle: {
                    opacity: 0,
                },
            },
        },
        {
            id: "right_height_x",
            gridId: "right_height_grid",
            type: "time",
            axisLine: { show: false },
            axisTick: { show: false },
            axisLabel: { show: false },
            splitLine: { show: false },
            axisPointer: {
                label: {
                    show: false,
                },
                lineStyle: { opacity: 0 },
            },
        },
        {
            id: "stagger_x",
            gridId: "stagger_grid",
            type: "time",
            axisLine: { show: false },
            axisTick: { show: false },
            axisLabel: { show: false },
            splitLine: { show: false },
            axisPointer: {
                label: {
                    show: false,
                },
                lineStyle: { opacity: 0 },
            },
        },
        {
            id: "right_stagger_x",
            gridId: "right_stagger_grid",
            type: "time",
            axisLine: { show: false },
            axisTick: { show: false },
            axisLabel: { show: false },
            splitLine: { show: false },
            axisPointer: {
                label: {
                    show: false,
                },
                lineStyle: { opacity: 0 },
            },
        },
        {
            id: "acceleration_x",
            gridId: "acceleration_grid",
            type: "time",
            axisLine: { show: false },
            axisTick: { show: false },
            axisLabel: { show: false },
            splitLine: { show: false },
            axisPointer: {
                label: {
                    show: false,
                },
                lineStyle: { opacity: 0 },
            },
        },
        {
            id: "right_acceleration_x",
            gridId: "right_acceleration_grid",
            type: "time",
            axisLine: { show: false },
            axisTick: { show: false },
            axisLabel: { show: false },
            splitLine: { show: false },
            axisPointer: {
                label: {
                    show: false,
                },
                lineStyle: { opacity: 0 },
            },
        },
        {
            id: "position_x",
            gridId: "position_grid",
            type: "time",
            axisLine: { show: false },
            axisTick: { show: false },
            axisLabel: { show: false },
            splitLine: { show: false },
            axisPointer: {
                label: {
                    show: false,
                },
            },
        },
        {
            id: "overview_x",
            type: "time",
            min: "dataMin",
            max: "dataMax",
            gridId: "overview_grid",
            axisLine: { show: false },
            axisTick: { show: false },
            axisLabel: { show: false },
            splitLine: { show: false },
            axisPointer: {
                show: true,
                snap: false,
                label: { show: false },
            },
            tooltip: { show: false },
        },
    ],
    yAxis: [
        {
            id: "height_y",
            gridId: "height_grid",
            type: "value",
            name: "Height (px)",
            nameLocation: "center",
            nameTextStyle: { fontSize: 14, fontWeight: "bold" },
            nameGap: 50,
            nameRotate: 90,
            min: SERIES_HEIGHT_MIN_Y,
            max: SERIES_HEIGHT_MAX_Y,
            axisPointer: { show: false },
        },
        {
            id: "right_height_y",
            gridId: "right_height_grid",
            show: false,
            type: "value",
            name: "Right Height (px)",
            nameLocation: "center",
            nameTextStyle: { fontSize: 14, fontWeight: "bold" },
            nameGap: 50,
            nameRotate: 90,
            min: SERIES_HEIGHT_MIN_Y,
            max: SERIES_HEIGHT_MAX_Y,
            axisPointer: { show: false },
        },
        {
            id: "stagger_y",
            gridId: "stagger_grid",
            type: "value",
            name: "Stagger (px)",
            nameLocation: "center",
            nameTextStyle: { fontSize: 14, fontWeight: "bold" },
            nameGap: 50,
            nameRotate: 90,
            min: SERIES_STAGGER_MIN_Y,
            max: SERIES_STAGGER_MAX_Y,
            axisPointer: { show: false },
        },
        {
            id: "right_stagger_y",
            gridId: "right_stagger_grid",
            show: false,
            type: "value",
            name: "Right Stagger (px)",
            nameLocation: "center",
            nameTextStyle: { fontSize: 14, fontWeight: "bold" },
            nameGap: 50,
            nameRotate: 90,
            min: SERIES_STAGGER_MIN_Y,
            max: SERIES_STAGGER_MAX_Y,
            axisPointer: { show: false },
        },
        {
            id: "acceleration_y",
            gridId: "acceleration_grid",
            type: "value",
            name: "Vertical Acceleration px/s²",
            nameLocation: "center",
            nameTextStyle: { fontSize: 14, fontWeight: "bold" },
            nameGap: 50,
            nameRotate: 90,
            min: -10000,
            max: 10000,
            axisPointer: { show: false },
        },
        {
            id: "right_acceleration_y",
            gridId: "right_acceleration_grid",
            show: false,
            type: "value",
            name: "Right V. Accel px/s²",
            nameLocation: "center",
            nameTextStyle: { fontSize: 14, fontWeight: "bold" },
            nameGap: 50,
            nameRotate: 90,
            min: -10000,
            max: 10000,
            axisPointer: { show: false },
        },
        {
            id: "position_y",
            show: false,
            gridId: "position_grid",
            type: "value",
            name: "Current Position",
            min: 0,
            max: 1,
            axisPointer: { show: false },
        },
        {
            id: "overview_y",
            min: 0,
            max: 1,
            gridId: "overview_grid",
            axisLine: { show: false, onZero: false },
            axisTick: { show: false },
            axisLabel: { show: false },
            splitLine: { show: false },
            axisPointer: { show: false },
        },
    ],
    dataset: [
        {
            id: "overview_dataset",
            source: [],
            dimensions: [
                { name: "name", type: "ordinal" },
                { name: "startTimestamp", type: "time" },
                { name: "endTimestamp", type: "time" },
                { name: "startPosition", type: "number" },
                { name: "endPosition", type: "number" },
            ],
        },
        ...["left", "right"].flatMap((channel) =>
            EXCEEDANCE_CLASSES.map((exceedanceClass) => ({
                id: `${channel}_${exceedanceClass}_exceedance_dataset`,
                source: [],
                dimensions: [
                    { name: "startTimestamp", type: "time" },
                    { name: "peakTimestamp", type: "time" },
                    { name: "endTimestamp", type: "time" },
                    { name: "exceedance", type: "number" },
                ],
            })),
        ),
        {
            id: "arcing_dataset",
            source: [],
            dimensions: [{ name: "peakTimestamp", type: "time" }],
        },
    ],
    series: [
        {
            type: "custom",
            id: "selected_timestamp_position_series",
            xAxisId: "position_x",
            yAxisId: "position_y",
            data: [],
            renderItem: function (params, api) {
                const index = api.value(0);
                const startPoint = api.coord([index, 1]);
                const height = api.size([0, 1])[1];

                const bounds = ecGraphic.clipRectByRect(
                    {
                        x: startPoint[0],
                        y: startPoint[1],
                        width: 0,
                        height: height,
                    },
                    {
                        x: params.coordSys.x,
                        y: params.coordSys.y,
                        width: params.coordSys.width,
                        height: params.coordSys.height,
                    },
                );

                return (
                    bounds && {
                        type: "line",
                        transition: ["shape"],
                        emphasisDisabled: true,
                        shape: {
                            x1: bounds.x,
                            x2: bounds.x,
                            y1: bounds.y,
                            y2: bounds.y + bounds.height,
                        },
                        style: {
                            stroke: "red",
                            lineWidth: 1,
                        },
                    }
                );
            },
            emphasis: {
                disabled: true,
            },
            animation: false,
            tooltip: { show: false },
        },
        {
            type: "scatter",
            id: "left_height_series",
            name: "Left height",
            xAxisId: "height_x",
            yAxisId: "height_y",
            symbolSize: 4,
            data: [],
            itemStyle: {
                color: "#e11d48", // red
            },
            large: true,
            animation: false,
            emphasis: { disabled: true },
            silent: true,
            markLine: thresholdMarkLines,
        },
        {
            type: "scatter",
            id: "right_height_series",
            name: "Right height",
            xAxisId: "height_x",
            yAxisId: "height_y",
            symbolSize: 4,
            data: [],
            itemStyle: {
                color: "#3b82f6", // blue
            },
            large: true,
            animation: false,
            emphasis: { disabled: true },
            silent: true,
            markLine: thresholdMarkLines,
        },
        {
            type: "scatter",
            id: "left_stagger_series",
            name: "Left stagger",
            xAxisId: "stagger_x",
            yAxisId: "stagger_y",
            symbolSize: 4,
            symbol: "circle",
            data: [],
            itemStyle: {
                color: "#f97316", // orange
            },
            large: true,
            animation: false,
            emphasis: { disabled: true },
            silent: true,
            markLine: thresholdMarkLines,
        },
        {
            type: "scatter",
            id: "right_stagger_series",
            name: "Right stagger",
            xAxisId: "stagger_x",
            yAxisId: "stagger_y",
            symbolSize: 4,
            symbol: "circle",
            data: [],
            itemStyle: {
                color: "#65a30d", // green
            },
            large: true,
            animation: false,
            emphasis: { disabled: true },
            silent: true,
            markLine: thresholdMarkLines,
        },
        {
            type: "line",
            id: "left_acceleration_series",
            name: "Left vertical acceleration",
            xAxisId: "acceleration_x",
            yAxisId: "acceleration_y",
            data: [],
            itemStyle: {
                color: "#ec4899", // pink
                opacity: 0,
            },
            lineStyle: {
                color: "#ec4899", // pink
                width: 2,
            },
            large: true,
            animation: false,
            emphasis: { disabled: true },
            silent: true,
            markLine: thresholdMarkLines,
        },
        {
            type: "line",
            id: "right_acceleration_series",
            name: "Right vertical acceleration",
            xAxisId: "acceleration_x",
            yAxisId: "acceleration_y",
            symbol: "none",
            data: [],
            itemStyle: {
                color: "#9333ea", // purple
                opacity: 0,
            },
            lineStyle: {
                color: "#9333ea", // purple
                width: 2,
            },
            large: true,
            animation: false,
            emphasis: { disabled: true },
            silent: true,
            markLine: thresholdMarkLines,
        },
        {
            type: "custom",
            id: "overview_series",
            datasetId: "overview_dataset",
            xAxisId: "overview_x",
            yAxisId: "overview_y",
            encode: {
                x: ["startTimestamp", "endTimestamp"],
                value: ["name", "startTimestamp", "endTimestamp", "startPosition", "endPosition"],
            },
            renderItem: (params, api) => {
                const routeName = api.value(0);
                const startTimestamp = api.value(1);
                const endTimestamp = api.value(2);
                const startPosition = api.value(3);
                const endPosition = api.value(4);

                const startMile = Math.floor(startPosition / 80);
                const endMile = Math.floor(endPosition / 80);

                const startChain = Math.floor((startPosition - startMile * 80) / 10);
                const endChain = Math.floor((endPosition - endMile * 80) / 10);

                const startPoint = api.coord([startTimestamp, 0.75]);
                const endPoint = api.coord([endTimestamp, 0.25]);

                const bounds = ecGraphic.clipRectByRect(
                    {
                        x: startPoint[0],
                        y: startPoint[1],
                        width: endPoint[0] - startPoint[0],
                        height: endPoint[1] - startPoint[1],
                    },
                    {
                        x: params.coordSys.x,
                        y: params.coordSys.y,
                        width: params.coordSys.width,
                        height: params.coordSys.height,
                    },
                );

                if (!bounds) {
                    return null;
                }

                const fullElrText = `${routeName} ${startMile}.${startChain} - ${endMile}.${endChain}`;
                const elrText = `${routeName}`;
                const fullTextWidth = ecFormat.getTextRect(fullElrText).width;
                const smallTextWidth = ecFormat.getTextRect(elrText).width;

                let text = "";
                if (bounds.width > fullTextWidth + 4) {
                    text = fullElrText;
                } else if (bounds.width > smallTextWidth + 4) {
                    text = elrText;
                }

                return {
                    type: "group",
                    transition: ["shape"],
                    children: [
                        {
                            type: "line",
                            transition: ["shape"],
                            ignore: !bounds,
                            shape: {
                                x1: bounds.x,
                                y1: bounds.y,
                                x2: bounds.x,
                                y2: bounds.y + bounds.height,
                            },
                            emphasis: { disabled: true },
                            style: {
                                stroke: "#000",
                                lineWidth: 1,
                            },
                        },
                        {
                            type: "rect",
                            transition: ["shape"],
                            ignore: !bounds,
                            shape: bounds,
                            emphasis: { disabled: true },
                            style: {
                                text,
                                fill: "rgba(255, 255, 255, 0.5)",
                                textFill: "#000",
                            },
                        },
                    ],
                };
            },
            emphasis: { disabled: true },
            animation: false,
            tooltip: { show: false },
        },
        ...["left", "right"].flatMap((channel) =>
            EXCEEDANCE_CLASSES.map((exceedanceClass) => ({
                type: "custom",
                id: `${channel}_${exceedanceClass}_exceedance_series`,
                datasetId: `${channel}_${exceedanceClass}_exceedance_dataset`,
                xAxisId: `${exceedanceClass}_x`,
                yAxisId: `${exceedanceClass}_y`,
                encode: {
                    x: ["startTimestamp", "endTimestamp"],
                    value: ["startTimestamp", "peakTimestamp", "endTimestamp", "exceedance"],
                },
                renderItem: renderExceedanceMarker,
                tooltip: { show: false },
            })),
        ),
        {
            type: "custom",
            id: `arc_exceedance_series`,
            datasetId: "arcing_dataset",
            xAxisId: "overview_x",
            yAxisId: "overview_y",
            encode: {
                x: "peakTimestamp",
                value: ["peakTimestamp"],
            },
            renderItem: (params, api) => {
                const point = api.coord([api.value("peakTimestamp"), 0.9]);

                return {
                    type: "text",
                    style: {
                        text: "⚡️",
                        x: point[0],
                        y: point[1],
                        textAlign: "center",
                        textVerticalAlign: "middle",
                        fontSize: 10,
                    },
                };
            },
            tooltip: { show: false },
        },
        {
            type: "custom",
            id: "current_view_series",
            xAxisId: "overview_x",
            yAxisId: "overview_y",
            data: [],
            renderItem: function (params, api) {
                const startIndex = api.value(0);
                const endIndex = api.value(1);

                const startPoint = api.coord([startIndex, 0.85]);
                const endPoint = api.coord([endIndex, 0.15]);

                const bounds = ecGraphic.clipRectByRect(
                    {
                        x: startPoint[0],
                        y: startPoint[1],
                        width: endPoint[0] - startPoint[0],
                        height: endPoint[1] - startPoint[1],
                    },
                    {
                        x: params.coordSys.x,
                        y: params.coordSys.y,
                        width: params.coordSys.width,
                        height: params.coordSys.height,
                    },
                );

                return (
                    bounds && {
                        type: "rect",
                        ignore: !bounds,
                        transition: ["shape"],
                        shape: bounds,
                        emphasis: { disabled: true },
                        style: {
                            fill: "rgba(220,192,255,0.5)",
                            stroke: "rgba(220,192,255,0.9)",
                        },
                    }
                );
            },
            emphasis: { disabled: true },
            animation: false,
            tooltip: { show: false },
        },
        {
            type: "custom",
            id: "selected_timestamp_overview_series",
            xAxisId: "overview_x",
            yAxisId: "overview_y",
            data: [],
            renderItem: function (params, api) {
                const index = api.value(0);

                const startPoint = api.coord([index, 0.9]);
                const endPoint = api.coord([index, 0.1]);

                const bounds = ecGraphic.clipRectByRect(
                    {
                        x: startPoint[0],
                        y: startPoint[1],
                        width: 0,
                        height: endPoint[1] - startPoint[1],
                    },
                    {
                        x: params.coordSys.x,
                        y: params.coordSys.y,
                        width: params.coordSys.width,
                        height: params.coordSys.height,
                    },
                );

                return (
                    bounds && {
                        type: "line",
                        transition: ["shape"],
                        emphasisDisabled: true,
                        shape: {
                            x1: bounds.x,
                            x2: bounds.x,
                            y1: bounds.y,
                            y2: bounds.y + bounds.height,
                        },
                        style: {
                            stroke: "red",
                            lineWidth: 1,
                        },
                    }
                );
            },
            emphasis: {
                disabled: true,
            },
            animation: false,
            tooltip: { show: false },
        },
    ],
};
